import retry from 'utils/retry';
import { logWarn, logInfo } from 'logging';

export const RESTART_IDLE_INTERVAL_MILLISECONDS = 9 * 60 * 1000; // 9 minutes
export const RECONNECT_INTERVAL_MILLISECONDS = 105 * 60 * 1000; // 105 minutes

const createWebSocket = async (getRoomAccess, onMessage, onError = null) => {
  let ws;

  const _sendRestartIdleIntervalMessage = () => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send('Restart IDLE Connection Timeout');
    }
  };

  const _connect = async () => {
    const roomAccess = await retry(getRoomAccess, 2, 1000);
    const { accessToken, websocketUrl } = roomAccess;
    logInfo(
      `BentoStream - Online Ordering - WS connection attempt with accessToken ${accessToken}`
    );
    ws = new WebSocket(`${websocketUrl}?accessToken=${accessToken}`);

    // Override close() method so reconnection interval is cleared out
    ws._close = ws.close;
    ws.close = () => {
      clearInterval(_reconnectInterval);
      clearInterval(_sendKeepAliveMessageInterval);
      ws._close();
    };

    _addEventListeners(roomAccess.accessToken);
  };

  const _reconnect = async () => {
    ws._close();
    await _connect();
  };

  const _restartIdleConnectionInterval = () => {
    clearInterval(_sendKeepAliveMessageInterval);
    _sendKeepAliveMessageInterval = setInterval(
      _sendRestartIdleIntervalMessage,
      RESTART_IDLE_INTERVAL_MILLISECONDS
    );
  };

  const _addEventListeners = () => {
    ws.addEventListener('message', event => {
      onMessage(event);
      _restartIdleConnectionInterval();
    });

    ws.addEventListener('close', event => {
      clearInterval(_sendKeepAliveMessageInterval);
      logInfo(
        'BentoStream - Online Ordering - WS connection closed',
        event.target.url
      );
    });

    ws.addEventListener('error', errorEvent => {
      logWarn(
        'BentoStream - Online Ordering - WS connection error',
        errorEvent.target.url,
        errorEvent
      );
      if (onError) {
        onError(errorEvent);
      }
    });

    ws.addEventListener('open', event => {
      logInfo(
        'BentoStream - Online Ordering - WS connection open',
        event.target.url
      );
    });
  };

  await _connect();

  /* Amazon API Gateway has two limitations that cannot be changed:
   * - 'Idle Connection Timeout' of 10 minutes: To avoid hitting the IDLE
   *   timeout, the WebSocket client posts a message to the connection every
   *   9 minutes of idle time.
   * - 'Connection duration for WebSocket API' of 2 hours: Before hitting the
   *   max duration of the connection we reconnect every interval of 90 minutes.
   * See: https://docs.aws.amazon.com/apigateway/latest/developerguide/limits.html
   */
  let _sendKeepAliveMessageInterval = setInterval(
    _sendRestartIdleIntervalMessage,
    RESTART_IDLE_INTERVAL_MILLISECONDS
  );
  const _reconnectInterval = setInterval(
    _reconnect,
    RECONNECT_INTERVAL_MILLISECONDS
  );

  return ws;
};

export default createWebSocket;
